/**
 * /styleguide: how Readlang's controls look and when to use each, drawn with
 * the components themselves (ui/), so it can't drift from the app. The
 * website and the iOS app share them; where the app draws iOS's own instead
 * (menus, selects' pickers, alerts), it says so.
 */
import * as preact from "preact";

import * as genericModals from "genericModals";
import { type PreactPageProps } from "pages/PreactPage";
import Button from "ui/Button";
import Chips from "ui/Chips";
import Field from "ui/Field";
import Menu from "ui/Menu";
import Popover from "ui/Popover";
import SearchField from "ui/SearchField";
import SegmentedControl from "ui/SegmentedControl";
import Select from "ui/Select";
import Sheet, { showSheet } from "ui/Sheet";
import DropdownMenu from "views/DropdownMenu";

interface State {
  sort: string;
  ascending: boolean;
  view: string;
  level: string;
  categories: string[];
  videoOnly: boolean;
  /** The search field's: what's typed, and searched for */
  typed: string;
  searched: string;
  /** The popover open, if any: one asked for, or one pointing */
  popover?: "asked" | "pointing";
}

const colors: { name: string; use: string }[] = [
  { name: "green", use: "Brand, headers, switches on, success" },
  { name: "blue-dark1", use: "Primary buttons, links, picked options" },
  { name: "blue-dark2", use: "Links; primary buttons hovered" },
  { name: "red", use: "Deleting, errors" },
  { name: "orange", use: "Warnings, streaks" },
  { name: "paper", use: "The page's background" },
  { name: "white", use: "Cards, controls, menus" },
  { name: "grey", use: "Controls' outlines" },
  { name: "grey-dark3", use: "Secondary text, field labels" },
  { name: "standard-text-color", use: "Text" },
];

/** An example, with how to make it */
class Example extends preact.Component<{
  code: string;
  children: preact.ComponentChildren;
}> {
  render() {
    return (
      <div class="styleGuideExample">
        <div class="styleGuideDemo">{this.props.children}</div>
        <pre>
          <code>{this.props.code}</code>
        </pre>
      </div>
    );
  }
}

class Section extends preact.Component<{
  title: string;
  intro?: preact.ComponentChildren;
  children: preact.ComponentChildren;
}> {
  render() {
    return (
      <section class="styleGuideSection">
        <h2>{this.props.title}</h2>
        {this.props.intro ? <p>{this.props.intro}</p> : null}
        {this.props.children}
      </section>
    );
  }
}

export default class StyleGuidePageView extends preact.Component<
  PreactPageProps,
  State
> {
  state: State = {
    sort: "title",
    ascending: true,
    view: "combined",
    level: "intermediate",
    categories: [],
    videoOnly: false,
    typed: "",
    searched: "",
  };

  componentDidMount() {
    document.title = "Style guide - Readlang";
    this.props.renderedCallback?.();
  }

  private sortBy = (sort: string) => {
    this.setState({
      sort,
      ascending: sort === this.state.sort ? !this.state.ascending : true,
    });
  };

  private renderPopover(popover: "asked" | "pointing", label: string) {
    const open = this.state.popover === popover;
    return (
      <Popover
        align="start"
        role="dialog"
        aria-label="3 day streak"
        arrow={popover === "pointing"}
        open={open}
        onClose={() => this.setState({ popover: undefined })}
        anchor={
          <Button
            aria-expanded={open}
            onClick={() =>
              this.setState({ popover: open ? undefined : popover })
            }
          >
            {label}
          </Button>
        }
      >
        <h4>3 day streak</h4>
        <p>Continue reading or practicing to meet today's goal!</p>
        Words read: 132
      </Popover>
    );
  }

  private showExampleSheet = () => {
    showSheet(
      <Sheet title="Filters" leading={{ label: "Clear", onClick: () => {} }}>
        <Field label="Reading level">
          <Select
            size="large"
            options={[
              { value: "beginner", label: "Beginner" },
              { value: "intermediate", label: "Intermediate" },
            ]}
            value="beginner"
            onChange={() => {}}
          />
        </Field>
        <label class="switchField">
          Only texts with video
          <input type="checkbox" class="switch" />
        </label>
      </Sheet>
    );
  };

  render() {
    const sortOptions = [
      { value: "title", label: "Title" },
      { value: "length", label: "Length" },
      { value: "lastRead", label: "Last Read" },
    ];
    return (
      <div class="styleGuide">
        <h1>Style guide</h1>
        <p class="styleGuideLead">
          One look for the website and the iOS app: Readlang's own, with iOS's
          menus, pickers and sheets where the app can use them. Use these
          components (<code>public/src/ui/</code>) rather than styling buttons,
          selects or menus afresh; their styles are in <code>_uiButton</code>,{" "}
          <code>_buttons</code>, <code>_forms</code>, <code>_menu</code> and{" "}
          <code>_dialogs.scss</code>, sized by the tokens in{" "}
          <code>_constants.scss</code>. Everything floating over the page
          (menus, popovers) is on one surface: <code>menuSurface</code>, frosted
          white.
        </p>

        <Section
          title="Colours"
          intro="Use the tokens in _constants.scss, not new hex codes."
        >
          <div class="styleGuideSwatches">
            {colors.map((color) => (
              <div class="styleGuideSwatch" key={color.name}>
                <span class={`swatch ${color.name}`}></span>
                <code>${color.name}</code>
                <small>{color.use}</small>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Type" intro="Jost throughout.">
          <h1>Heading 1 ($font-h1)</h1>
          <h2>Heading 2 ($font-h2)</h2>
          <h3>Heading 3</h3>
          <p>Body text, $font-medium: the quick brown fox jumps.</p>
          <p class="styleGuideSmall">
            Controls are $font-small with a mouse and $font-medium on touch
            screens (any smaller and iOS zooms into a field), so a button and
            the select beside it always match.
          </p>
        </Section>

        <Section
          title="Buttons"
          intro="Secondary for most; one primary for a screen's main action; plain in a line of text or a sheet's header. Medium (36px) in toolbars, large (44px, a finger's touch target) in forms."
        >
          <Example
            code={`<Button>Shelves…</Button>\n<Button kind="primary">Upload</Button>\n<Button kind="plain">Clear all</Button>`}
          >
            <Button>Shelves…</Button>
            <Button kind="primary">Upload</Button>
            <Button kind="plain">Clear all</Button>
          </Example>
          <Example
            code={`<Button icon="icon-filter" active chevron="down">Filters <span class="badge">2</span></Button>\n<Button icon="icon-sort-by-attributes" aria-label="Sort" />\n<Button kind="primary" disabled>Actions</Button>\n<Button destructive>Delete</Button>`}
          >
            <Button icon="icon-filter" active chevron="down">
              Filters <span class="badge">2</span>
            </Button>
            <Button icon="icon-sort-by-attributes" aria-label="Sort" />
            <Button kind="primary" disabled chevron="down">
              Actions
            </Button>
            <Button destructive>Delete</Button>
          </Example>
          <Example
            code={`<Button kind="primary" size="large" block>Generate Story</Button>`}
          >
            <Button kind="primary" size="large" block>
              Generate Story
            </Button>
          </Example>
          <p class="styleGuideNote">
            Older code names primary buttons <code>.prominent</code> (and a
            form's full-width one <code>.payButton</code>). They look the same;
            use <code>Button</code> in new code. The Web Reader's own
            stylesheets still draw those names.
          </p>
        </Section>

        <Section
          title="Selects"
          intro="The platform's own select, drawn as a control with a chevron: in the app, choosing opens iOS's menu. Every <select> looks like this; Select adds an icon, sizes and full width."
        >
          <Example
            code={`<Select icon="icon-sort-by-attributes" options={…} value={…} onChange={…} />\n<Select size="large" block options={…} … />`}
          >
            <Select
              icon="icon-sort-by-attributes"
              aria-label="Sort by"
              options={[
                { value: "hot", label: "Hot" },
                { value: "top", label: "Top" },
              ]}
              value="hot"
              onChange={() => {}}
            />
            <Select
              aria-label="Show"
              options={[{ value: "queue", label: "To Read (15)" }]}
              value="queue"
              onChange={() => {}}
            />
          </Example>
          <Example code={`<Select size="large" block … />`}>
            <Select
              size="large"
              block
              aria-label="Reading level"
              options={[
                { value: "beginner", label: "Beginner" },
                { value: "intermediate", label: "Intermediate" },
              ]}
              value={this.state.level}
              onChange={(level) => this.setState({ level })}
            />
          </Example>
        </Section>

        <Section
          title="A list's toolbar"
          intro="Controls in a row share one height: select all, Actions and the sort menu over a list (.listControls), a search field (ui/SearchField) beside them. It searches on Enter, with no button taking room in the row: one shows under it only while what's typed hasn't been searched for."
        >
          <div class="styleGuideDemo">
            <div class="listControls">
              <div class="selectAllContainer">
                <input type="checkbox" aria-label="Select all" />
              </div>
              <Button kind="primary" chevron="down" disabled>
                Actions
              </Button>
              <SearchField
                value={this.state.typed}
                query={this.state.searched}
                placeholder="Search"
                onInput={(typed) => this.setState({ typed })}
                onSearch={(searched) => this.setState({ searched })}
              />
              <Button icon="icon-sort-by-attributes" aria-label="Sort" />
            </div>
          </div>
        </Section>

        <Section
          title="Menus"
          intro="Actions, or options to pick, from a button. In the app they're iOS's own menus; on the web this popover, drawn as iOS's. Sections are split by a line and may have a title; a tick marks what's picked, and picking a sort again reverses it (its arrow showing which way). Deleting is red. No headings like “Perform action”."
        >
          <Example
            code={`<Menu sections={[{ title: "Sort by", items: [{ label, checked, direction, onSelect }] }]}\n  renderButton={(props) => <Button {...props} icon="icon-sort-by-attributes" />} />`}
          >
            <Menu
              align="start"
              sections={[
                {
                  title: "Sort by",
                  items: sortOptions.map((option) => ({
                    label: option.label,
                    checked: option.value === this.state.sort,
                    direction:
                      option.value === this.state.sort
                        ? this.state.ascending
                          ? "ascending"
                          : "descending"
                        : undefined,
                    onSelect: () => this.sortBy(option.value),
                  })),
                },
              ]}
              renderButton={(buttonProps) => (
                <Button {...buttonProps} icon="icon-sort-by-attributes">
                  Sort
                </Button>
              )}
            />
            <Menu
              align="start"
              sections={[
                {
                  items: [
                    { label: "Mark as Completed", onSelect: () => {} },
                    { label: "Archive Texts", onSelect: () => {} },
                  ],
                },
                {
                  items: [
                    { label: "Rename Text", disabled: true },
                    {
                      label: "Delete Texts",
                      destructive: true,
                      onSelect: () => {},
                    },
                  ],
                },
              ]}
              renderButton={(buttonProps) => (
                <Button {...buttonProps} kind="primary" chevron="down">
                  Actions
                </Button>
              )}
            />
            <DropdownMenu
              items={[
                {
                  type: "toggle",
                  label: "Auto-Pronounce",
                  initialValue: true,
                  onToggle: () => {},
                },
                { type: "separator" },
                {
                  type: "action",
                  label: "Report this text",
                  onClick: () => {},
                },
              ]}
            />
          </Example>
          <p class="styleGuideNote">
            The ⋯ menu (<code>DropdownMenu</code>) is a page's own options: ⋯ in
            the app, as iOS's, and ⋮ on the web.
          </p>
        </Section>

        <Section
          title="Popovers"
          intro="A little content that isn't a menu (the streak's day, Public Texts' filters, a header language's picker) floats over the page from what opened it, in a popover on the menus' surface: a heading, then a little text. A press outside or Escape closes it. One opened unasked points at what it's about with an arrow. Too big for a popover, or on phones where it has no room, it's a sheet instead."
        >
          <Example
            code={`<Popover open={open} onClose={close} anchor={<Button onClick={toggle}>…</Button>}>\n  <h4>3 day streak</h4>\n  <p>…</p>\n</Popover>`}
          >
            {this.renderPopover("asked", "Open a popover")}
            {this.renderPopover("pointing", "One pointing")}
          </Example>
        </Section>

        <Section
          title="Forms"
          intro="A label above each field (Field), large controls, a hint below. Two fields side by side in .fieldPair; a range in .fieldRange; on/off options as switches (.switchField); any of a few options as Chips."
        >
          <div class="styleGuideDemo styleGuideForm">
            <Field
              label="Name of story"
              for="styleGuideName"
              hint="e.g. The Three Little Pigs"
            >
              <input
                id="styleGuideName"
                class="input large"
                placeholder="Type name of story"
              />
            </Field>
            <div class="fieldPair">
              <Field label="Reading level">
                <Select
                  size="large"
                  options={[{ value: "beginner", label: "Beginner" }]}
                  value="beginner"
                  onChange={() => {}}
                />
              </Field>
              <Field label="Length (approx.)">
                <Select
                  size="large"
                  options={[{ value: "800", label: "800 words" }]}
                  value="800"
                  onChange={() => {}}
                />
              </Field>
            </div>
            <Field label="Length (words)">
              <div class="fieldRange">
                <Select
                  aria-label="From"
                  size="large"
                  options={[{ value: "0", label: "0" }]}
                  value="0"
                  onChange={() => {}}
                />
                to
                <Select
                  aria-label="To"
                  size="large"
                  options={[{ value: "Infinity", label: "10,000+" }]}
                  value="Infinity"
                  onChange={() => {}}
                />
              </div>
            </Field>
            <Field label="Category">
              <Chips
                options={[
                  { value: "Fiction", label: "Fiction" },
                  { value: "Non-Fiction", label: "Non-Fiction" },
                  { value: "Song", label: "Song" },
                ]}
                selected={this.state.categories}
                onToggle={(value, picked) =>
                  this.setState({
                    categories: picked
                      ? [...this.state.categories, value]
                      : this.state.categories.filter(
                          (category) => category !== value
                        ),
                  })
                }
              />
            </Field>
            <label class="switchField">
              Only texts with video
              <input
                type="checkbox"
                class="switch"
                checked={this.state.videoOnly}
                onChange={(event) =>
                  this.setState({ videoOnly: event.currentTarget.checked })
                }
              />
            </label>
          </div>
        </Section>

        <Section
          title="Segmented control"
          intro="One of a few views of the same thing, as iOS's."
        >
          <Example
            code={`<SegmentedControl options={…} value={…} onChange={…} />`}
          >
            <SegmentedControl
              options={[
                { value: "combined", label: "Combined" },
                { value: "wordsRead", label: "Words read" },
                { value: "wordsPracticed", label: "Words practiced" },
              ]}
              value={this.state.view}
              onChange={(view) => this.setState({ view })}
            />
          </Example>
        </Section>

        <Section
          title="Sheets and dialogs"
          intro="A form or set of options too big for a menu opens in a sheet: its title between its actions (Clear, Done). On phones it fills the screen, sliding up (in the app, over the tab bar); elsewhere it's a dialog. Small alerts and confirmations stay centred, and are iOS's own in the app."
        >
          <Example
            code={`showSheet(<Sheet title="Filters" leading={{ label: "Clear", onClick }}>…</Sheet>)`}
          >
            <Button onClick={this.showExampleSheet}>Open a sheet</Button>
            <Button
              onClick={() =>
                genericModals.alert({
                  title: "An alert",
                  message: "iOS's own in the app.",
                })
              }
            >
              Open an alert
            </Button>
          </Example>
        </Section>

        <Section title="Website and app">
          <table class="styleGuideTable">
            <thead>
              <tr>
                <th></th>
                <th>Website</th>
                <th>iOS app</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Menus</td>
                <td>This popover</td>
                <td>iOS's menus (a menu with a stepper stays the web's)</td>
              </tr>
              <tr>
                <td>Popovers</td>
                <td>Under what opened them; a sheet on phones if big</td>
                <td>The same, the web's</td>
              </tr>
              <tr>
                <td>Selects</td>
                <td>The browser's list</td>
                <td>iOS's menu</td>
              </tr>
              <tr>
                <td>Page menu</td>
                <td>⋮</td>
                <td>⋯</td>
              </tr>
              <tr>
                <td>Sheets</td>
                <td>Full screen on phones, a dialog elsewhere</td>
                <td>Full screen, over the tab bar</td>
              </tr>
              <tr>
                <td>Alerts</td>
                <td>A centred dialog</td>
                <td>iOS's alerts</td>
              </tr>
            </tbody>
          </table>
        </Section>
      </div>
    );
  }
}
